import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { AttributeValuesFormComponent } from '@src/app/shared/components/attribute-values-form/attribute-values-form.component';
import { DynamicAttributeDefinition } from '@src/app/shared/components/attribute-values-form/attribute-values-form.model';
import { RetailerAttributeValuesAdapter } from '@src/app/features/retailers/retailer-attribute-values.adapter';
import { RetailerRequest, RetailersService, MarketsService, RetailerTypesService, DistributionChannelsService, CompanyResponse, BusinessGroupsService, BrandsService, AttributeRequest } from '@src/app/shared/backend/clients';
import { CountryService } from '@src/app/shared/services/countries-service';
import { firstValueFrom } from 'rxjs';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { SessionService } from '@src/app/shared/services/session.service';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { PermissionService } from '@src/app/shared/services/permission.service';

@Component({
    selector: 'app-add-retailer',
    templateUrl: './add-retailer.component.html',
    styleUrl: './add-retailer.component.scss',
    host: {
        class: 'app-add-retailer'
    },
    standalone: false
})
export class AddRetailerComponent extends BaseViewComponent implements OnInit {

  @ViewChild('retailerAttributeValuesForm') retailerAttributeValuesForm?: AttributeValuesFormComponent;

  company: CompanyResponse | undefined;
  companyHasCpo: boolean = false;

  countries: any[] = [];
  languages: { language: string; code: string }[] = [];

  retailerAttributesFeatureEnabled = false;
  retailerAttributeDefinitions: DynamicAttributeDefinition[] = [];
  showDistributionChannel: boolean = false;

  combosData: { [key: string]: ComboData } = {
    'markets': new ComboData(async (filter) => await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(filter))),
    'retailerTypes': new ComboData(async (filter) => await firstValueFrom(this.retailerTypesService.apiRetailerTypesFilterComboPost(filter))),
    'businessGroups': new ComboData(async (filter) => await firstValueFrom(this.businessGroupsService.apiBusinessGroupsFilterComboPost(filter))),
    'distributionChannels': new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    'brands': new ComboData(async (filter) => {
      const activeBrandsFilter = {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      };
      const brandsFilter = {
        ...filter,
        additionalFilters: [
          ...(filter.additionalFilters ?? []),
          activeBrandsFilter
        ]
      };
      return await firstValueFrom(this.brandsService.apiBrandsFilterComboPost(brandsFilter));

    })
  };

  public retailerForm: FormGroup = this.fb.group({
    retailerName: ['', [Validators.required, Validators.maxLength(255)]],
    romanisedName: ['', [Validators.maxLength(255)]],
    externalRetailerId: ['', [Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    street: ['', [Validators.required, Validators.maxLength(255)]],
    zipCode: ['', [Validators.required, Validators.maxLength(50)]],
    city: ['', [Validators.required, Validators.maxLength(50)]],
    cityRomanized: ['', [latinCharsetRegexValidator()]],
    stateProvince: ['', [Validators.maxLength(255)]],
    stateProvinceRomanized: ['', [latinCharsetRegexValidator()]],
    country: [[], [Validators.required]],
    latitude: ['', [Validators.maxLength(50)]],
    longitude: ['', [Validators.maxLength(50)]],
    marketId: [[], [Validators.required]],
    language: [null, [Validators.maxLength(3)]],
    brandIds: [[]],
    retailerTypeId: [[]],
    businessGroupId: [[]],
    distributionChannelIds: [[]],
    openingDate: [null],
    closingDate: [null],
    hasCpo: ['false', [Validators.required]],
    email: ['', [emailRegexValidator(), Validators.maxLength(50)]],
    comments: ['', [Validators.maxLength(255)]]
  });

  filterOperator = FilterOperator;

  constructor(
    private session: SessionService,
    private router: Router,
    private translate: TranslateService,
    private retailersService: RetailersService,
    private brandsService: BrandsService,
    private retailerTypesService: RetailerTypesService,
    private businessGroupsService: BusinessGroupsService,
    private distributionChannelsService: DistributionChannelsService,
    private marketsService: MarketsService,
    private countryService: CountryService,
    private retailerAttributeValuesAdapter: RetailerAttributeValuesAdapter,
    private permissionService: PermissionService,
    private fb: FormBuilder) {
    super();
    this.getLanguages();
  }

  ngOnInit() {
    this.init();
  }
  private async init(): Promise<void> {
    this.company = await this.session.getCompanyData();
    this.countries = await this.countryService.getCountries();
    this.companyHasCpo = !!this.company?.hasCpo;
    this.showDistributionChannel = await this.permissionService.hasFeaturePermissions(Features.DistributionChannels);
    const distributionChannelControl = this.retailerForm.get('distributionChannelIds');
    if (this.showDistributionChannel) {
      distributionChannelControl?.setValidators([Validators.required]);
    } else {
      distributionChannelControl?.clearValidators();
    }

    const settings = await this.session.getCompanySettings();

    this.retailerAttributesFeatureEnabled =
      settings.features?.some(
        feature =>
          feature.name === 'RetailerAttributes' &&
          feature.isEnabled
      ) ?? false;

    await this.initCombosData();
    await this.loadRetailerAttributeDefinitions();

    this.initForm();
  }

  private async loadRetailerAttributeDefinitions(): Promise<void> {
    if (!this.retailerAttributesFeatureEnabled) {
      this.retailerAttributeDefinitions = [];
      return;
    }

    this.retailerAttributeDefinitions = await this.retailerAttributeValuesAdapter.getActiveDefinitions();
  }

  private initForm(): void {
    const brandsDropdown = this.combosData['brands'];
    const availableBrands = brandsDropdown.list ?? [];

    const selectedBrandIds =
      brandsDropdown.totalRecords === 1 && availableBrands.length === 1 ? [availableBrands[0].id] : [];

    this.retailerForm.patchValue({
      brandIds: selectedBrandIds
    });
  }

  private async initCombosData() {
    await this.combosData['brands'].getData(null, 1);
    await this.combosData['markets'].getData(null, 1);
    await this.combosData['retailerTypes'].getData(null, 1);
    await this.combosData['distributionChannels'].getData(null, 1);

    if (this.showBusinessGroup) {
      await this.combosData['businessGroups'].getData(null, 1);
    }
  }

  async onLazyLoadComboData(
    comboType: 'markets' | 'retailerTypes' | 'businessGroups' | 'distributionChannels' | 'brands',
    event: any
  ) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
  }

  getComboTotalPages(
    comboType: 'markets' | 'retailerTypes' | 'businessGroups' | 'distributionChannels' | 'brands'
  ): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private getLanguages() {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    const translation = this.translate.instant('PORTAL_LANGUAGES');
    const transformedLanguages = enabledLanguages.map(lang => {
      const code = lang.code || 'unknown';
      if (code === undefined || code === null) {
        console.warn(`Language code is missing for language: ${lang.name}`);
        return {
          language: 'Unknown',
          code: 'unknown'
        };
      }
      return {
        language: translation[code.toUpperCase()],
        code: code
      };
    });

    this.languages = transformedLanguages;
  }

  get showBusinessGroup(): boolean {
    return this.company?.hasRetailerBusinessGroup === true;
  }

  async submitForm() {

    this.retailerForm.markAllAsTouched();
    this.retailerAttributeValuesForm?.markAllAsTouched();

    const retailerAttributesFormIsValid =
      this.retailerAttributeValuesForm?.attributeValuesForm.valid ?? true;

    if (this.retailerForm.valid && retailerAttributesFormIsValid) {

      let retailerAttributes: AttributeRequest[] = [];

      if (
        this.retailerAttributesFeatureEnabled &&
        this.retailerAttributeValuesForm
      ) {
        const selections =
          this.retailerAttributeValuesForm.getSelections();

        retailerAttributes =
          this.retailerAttributeValuesAdapter.mapSelectionsToRequest(
            selections
          );
      }

      const distributionChannelIds = this.retailerForm.controls['distributionChannelIds'].value || [];

      const brandIds = this.retailerForm.controls['brandIds'].value || [];

      const utcOpeningDate = this.retailerForm.controls['openingDate'].value ? new Date(this.retailerForm.controls['openingDate'].value).toISOString() : null;
      const utcClosingDate = this.retailerForm.controls['closingDate'].value ? new Date(this.retailerForm.controls['closingDate'].value).toISOString() : null;

      const payload: RetailerRequest = {
        brandIds: brandIds,
        code: this.retailerForm.controls['code'].value,
        name: this.retailerForm.controls['retailerName'].value,
        street: this.retailerForm.controls['street'].value,
        zip: this.retailerForm.controls['zipCode'].value,
        city: this.retailerForm.controls['city'].value,
        stateProvince: this.retailerForm.controls['stateProvince'].value,
        isoCountry: this.retailerForm.controls['country'].value?.[0] ?? undefined,
        latitude: this.retailerForm.controls['latitude'].value,
        longitude: this.retailerForm.controls['longitude'].value,
        hasCpo: this.companyHasCpo ? this.retailerForm.controls['hasCpo'].value === 'true' : false,
        distributionChannelIds: distributionChannelIds,
        externalPosId: this.retailerForm.controls['externalRetailerId'].value,
        nameRomanised: this.retailerForm.controls['romanisedName'].value,
        cityRomanised: this.retailerForm.controls['cityRomanized'].value,
        stateProvinceRomanised: this.retailerForm.controls['stateProvinceRomanized'].value,
        openingDate: utcOpeningDate,
        closingDate: utcClosingDate,
        email: this.retailerForm.controls['email'].value,
        marketId: this.retailerForm.controls['marketId'].value?.[0],
        typeId: this.retailerForm.controls['retailerTypeId'].value?.[0],
        businessGroupId: this.showBusinessGroup ? this.retailerForm.controls['businessGroupId'].value?.[0] : undefined,
        comment: this.retailerForm.controls['comments'].value,
        retailerAttributes
      };

      await firstValueFrom(this.retailersService.apiRetailersCreatePost(payload));

      this.retailerForm.controls['brandIds'].setValue([]);
      this.retailerForm.controls['email'].setValue(null);
      this.retailerForm.controls['marketId'].setValue([]);
      this.retailerForm.controls['retailerTypeId'].setValue([]);
      this.retailerForm.controls['businessGroupId'].setValue([]);
      this.retailerForm.controls['comments'].setValue(null);
      this.retailerForm.controls['code'].setValue(null);
      this.retailerForm.controls['retailerName'].setValue(null);
      this.retailerForm.controls['street'].setValue(null);
      this.retailerForm.controls['zipCode'].setValue(null);
      this.retailerForm.controls['city'].setValue(null);
      this.retailerForm.controls['stateProvince'].setValue(null);
      this.retailerForm.controls['country'].setValue([]);
      this.retailerForm.controls['latitude'].setValue(null);
      this.retailerForm.controls['hasCpo'].setValue(null);
      this.retailerForm.controls['externalRetailerId'].setValue(null);
      this.retailerForm.controls['romanisedName'].setValue(null);
      this.retailerForm.controls['cityRomanized'].setValue(null);
      this.retailerForm.controls['stateProvinceRomanized'].setValue(null);

      this.router.navigate(['/retailers']);
    }
  }
}
